import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../../lib/api';

const ANALYTICS_STALE_TIME = 5 * 60 * 1000;
const ANALYTICS_GC_TIME = 30 * 60 * 1000;

function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  return Number.parseFloat(String(v)) || 0;
}

// Null is a real value in the analytics contract: it means "no data to compute
// this from", which is not the same as zero. Keep it null all the way to render.
function toNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  const parsed = Number.parseFloat(String(v));
  return Number.isNaN(parsed) ? null : parsed;
}

export interface DateRange {
  start: string;
  end: string;
}

// The analytics window, defined once. It mirrors the `period` enum on
// `DashboardSummaryQueryDto` and `PipelineVelocityQueryDto`, which is the only
// set of windows the API can serve: a range the type permits but the backend
// rejects gets silently answered with 30 days.
export type TimeRange = '7d' | '30d' | '90d';

const RANGE_DAYS: Record<TimeRange, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export function getDateRange(timeRange: TimeRange): DateRange {
  const end = new Date();
  const start = new Date();

  start.setDate(start.getDate() - RANGE_DAYS[timeRange]);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export interface TrafficSourceItem {
  source: string;
  visitors: number;
  percentage: string;
}

export interface LeadsOverTimePoint {
  date: string;
  count: number;
}

export interface LeadsOverTimeResponse {
  data: LeadsOverTimePoint[];
  total: number;
  granularity: 'day' | 'week' | 'month';
}

export interface QualificationBucket {
  label: string;
  lowerBound: number;
  upperBound: number;
  count: number;
}

export interface QualificationScoreDistribution {
  buckets: QualificationBucket[];
  scoredLeads: number;
  totalLeadsInWindow: number;
}

export interface DashboardSummaryResponse {
  leadsTrend: Array<{ date: string; count: number }>;
  sourceDistribution: Array<{
    source: string;
    count: number;
    percentage: number;
  }>;
  // Touches per weekday, Monday first, counted by the API over the selected
  // window. Optional for the same caching reason as the fields below: a payload
  // cached before this shipped carries none, and the card then shows nothing
  // rather than falling back to `leadsTrend`, which is what it used to plot.
  contactActivityByWeekday?: number[];
  stageDistribution: Array<{
    stageId: string;
    stageName: string;
    color: string;
    position: number;
    // Leads currently in the stage: occupancy, a state.
    count: number;
    // `won` and `lost` are terminal: those stages are closed outcomes, not open
    // pipeline. Null means unclassified, which means OPEN — most real stages
    // carry no role. Optional for the same caching reason as the fields below.
    semanticRole?: string | null;
    // Distinct leads ever in the stage during the window, and this stage's share
    // of the entry stage's population. For a TERMINAL stage that share counts
    // the leads standing there NOW instead: a milestone is permanent, an outcome
    // is reversible, and "ever reached Lost" pinned that row at 100% however
    // many leads were recovered. Optional for the same reason as
    // `qualificationScoreDistribution` below: a payload cached before this field
    // shipped carries neither, and an absent conversion is "no data" — never a
    // recomputation from occupancy, which is the bug this replaced.
    population?: number;
    conversionPct?: number | null;
  }>;
  // Optional because the summary is cached server-side for 5 minutes: right
  // after a deploy a client can still be handed a payload built before this
  // field existed. An absent distribution is "no data", never a stand-in curve.
  qualificationScoreDistribution?: QualificationScoreDistribution;
  kpis: {
    totalLeads: number;
    leadGrowth: number;
    conversionRate: number | null;
    conversionGrowth: number | null;
    // Null when the funnel has no open stage past its entry: there is no
    // pipeline to score. Not zero.
    pipelineValue: string | null;
    leadVelocityRate: number | null;
    // Null when nothing was won in the window: there is no cycle to average.
    avgCycleTimeDays: number | null;
    untouchedLeadsCount: number | null;
  };
}

export function useTrafficSources(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<TrafficSourceItem[]>({
    queryKey: ['analytics', 'traffic-sources', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getTrafficSources({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return data;
    },
    enabled: !!organizationId,
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
  });
}

export function useLeadsOverTime(
  organizationId: string | undefined,
  dateRange: DateRange,
  granularity: 'day' | 'week' | 'month' = 'day',
) {
  return useQuery<LeadsOverTimeResponse>({
    queryKey: ['analytics', 'leads-over-time', organizationId, dateRange, granularity],
    queryFn: async () => {
      const { data } = await analyticsAPI.getLeadsOverTime({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
        granularity,
      });
      return data;
    },
    enabled: !!organizationId,
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
  });
}

export function useDashboardSummary(organizationId: string | undefined, period: TimeRange) {
  return useQuery<DashboardSummaryResponse>({
    queryKey: ['analytics', 'dashboard-summary', organizationId, period],
    queryFn: async () => {
      const { data } = await analyticsAPI.getDashboardSummary({
        organizationId: organizationId!,
        period,
      });
      return {
        ...data,
        kpis: {
          ...data.kpis,
          totalLeads: toNum(data.kpis.totalLeads),
          leadGrowth: toNum(data.kpis.leadGrowth),
          conversionRate: toNumOrNull(data.kpis.conversionRate),
          conversionGrowth: toNumOrNull(data.kpis.conversionGrowth),
          leadVelocityRate: toNumOrNull(data.kpis.leadVelocityRate),
          avgCycleTimeDays: toNumOrNull(data.kpis.avgCycleTimeDays),
          untouchedLeadsCount: toNumOrNull(data.kpis.untouchedLeadsCount),
        },
      };
    },
    enabled: !!organizationId,
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
  });
}
